import {
  Enterprise,
  EnterprisePayload,
  OrganizationalUnit,
  OrganizationalUnitPayload,
  StructureTypeEnum,
} from '@/slices/enterprises/enterprise.types';

import { CompanyMetadata } from '@/types/companies.types';
import { StandardItemLink } from '@/types/generic.types';

export const normalizeEnterprise = (
  enterprise: EnterprisePayload,
): Enterprise => {
  return {
    ...enterprise,
    metadata: normalizeCompanyMetadata(enterprise.metadata),
    structureType: normalizeStructureType(enterprise.structureType),
    organizationalUnits: (enterprise.organizationalUnits ?? []).map(
      normalizeOrganizationalUnit,
    ),
    isTemplate: enterprise.isTemplate,
    isPublic: enterprise.isPublic,
    ownedBy: enterprise.ownedBy,
    archivedDate: enterprise.archivedDate ?? null,
    createdBy: enterprise.createdBy,
    license: enterprise.license,
    customStandards: normalizeCustomStandards(enterprise.customStandards),
    hasLicense: (enterprise.license?.licensesCount ?? 0) > 0,
    type: enterprise.type,
    isDisabled: enterprise.isDisabled,
  };
};

const normalizeCustomStandards = (
  customStandards: StandardItemLink[] | null | undefined,
): StandardItemLink[] => {
  if (!customStandards) {
    return [];
  }

  return customStandards.filter(
    (item): item is StandardItemLink =>
      typeof item.slug === 'string' && typeof item.type === 'string',
  );
};

const structureTypeValues = new Set<string>(Object.values(StructureTypeEnum));

const normalizeStructureType = (
  structureType: string | null,
): StructureTypeEnum | null => {
  if (!structureType || !structureTypeValues.has(structureType)) {
    return null;
  }

  return structureType as StructureTypeEnum;
};

const normalizeCompanyMetadata = (
  metadata: Record<string, unknown> | null,
): CompanyMetadata | null => {
  if (!metadata || Array.isArray(metadata)) {
    return null;
  }

  return {
    ...metadata,
    customQuestionTypes: Array.isArray(metadata.customQuestionTypes)
      ? metadata.customQuestionTypes.filter(
          (value): value is string => typeof value === 'string',
        )
      : null,
    size: typeof metadata.size === 'number' ? metadata.size : null,
    sector: typeof metadata.sector === 'string' ? metadata.sector : null,
    annualTurnover:
      typeof metadata.annualTurnover === 'string'
        ? metadata.annualTurnover
        : null,
    identificationNumber:
      typeof metadata.identificationNumber === 'string'
        ? metadata.identificationNumber
        : null,
  };
};

const normalizeOrganizationalUnit = (
  organizationalUnit: OrganizationalUnitPayload,
): OrganizationalUnit => {
  return {
    ...organizationalUnit,
    metadata: normalizeCompanyMetadata(organizationalUnit.metadata),
    structureType: normalizeStructureType(organizationalUnit.structureType),
    enterpriseId: organizationalUnit.enterpriseId ?? null,
    subsidiaries: (organizationalUnit.subsidiaries ?? []).map(
      normalizeOrganizationalUnit,
    ),
    type: organizationalUnit.type,
    isDisabled: organizationalUnit.isDisabled,
  };
};
